import React, { useEffect } from 'react';
import authService from './authService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Box, FormControl, Chip, Autocomplete } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Button } from 'components/index';

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const availableRoles = ['System Admin', 'CDCC Admin', 'Inspector'];

  const validationSchema = yup.object({
    firstName: yup
      .string()
      .required('First Name is required')
      .matches(/^[A-Za-z\s'-]+$/, 'First Name can only contain letters, spaces, apostrophes, and hyphens')
      .min(2, 'First Name must be at least 2 characters long')
      .trim()
      .strict(true)
      .test('no-spaces', 'First Name cannot contain only spaces', value => !!value?.trim()),
    lastName: yup
      .string()
      .nullable()
      .matches(/^[A-Za-z\s'-]*$/, 'Last Name can only contain letters, spaces, apostrophes, and hyphens')
      .test('min-length-if-provided', 'Last Name must be at least 2 characters long if provided', value => {
        if (!value || value.trim() === '') return true;
        return value.trim().length >= 2;
      })
      .trim(),
    email: yup
      .string()
      .email('Enter a valid email')
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Enter a valid email address')
      .required('Email is required'),
    roles: yup.array().of(yup.string()).min(1, 'At least one role must be selected'),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      roles: [] as string[],
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      setIsLoading(true);
      try {
        await authService.register(values.email, values.firstName, values.lastName, values.roles);
        toast.success('Registration successful! An email has been sent to the registered user.');
        onClose();
      } catch (error: any) {
        const errorMessage = error.message || error.response?.data?.message || 'Registration failed. Please try again.';
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create a New User</DialogTitle>
      <DialogContent>
        <Box component='form' onSubmit={formik.handleSubmit}>
          <Box mb={1}>
            <TextField
              type='text'
              label='First Name'
              variant='outlined'
              margin='normal'
              placeholder='John'
              fullWidth
              name='firstName'
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              required
            />
          </Box>
          <Box mb={1}>
            <TextField
              type='text'
              label='Last Name'
              variant='outlined'
              margin='normal'
              placeholder='Carter'
              fullWidth
              name='lastName'
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </Box>
          <Box mb={1}>
            <TextField
              type='email'
              label='Email'
              variant='outlined'
              margin='normal'
              placeholder='john@example.com'
              fullWidth
              name='email'
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email && formik.errors.email
                  ? 'Invalid email format. Please enter a valid email address in the format: example@domain.xyz'
                  : ''
              }
              required
            />
          </Box>
          <Box mb={1}>
            <FormControl fullWidth margin='normal' sx={{ marginTop: 2 }}>
              <Autocomplete
                multiple
                id='roles-autocomplete'
                options={availableRoles}
                value={formik.values.roles}
                onChange={(event, newValue) => {
                  formik.setFieldValue('roles', newValue);
                }}
                onBlur={formik.handleBlur}
                filterSelectedOptions
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip key={key} label={option} {...tagProps} size='small' style={{ backgroundColor: 'lightgray', color: 'black' }} />
                    );
                  })
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Roles'
                    placeholder='Select roles'
                    error={formik.touched.roles && Boolean(formik.errors.roles)}
                    helperText={formik.touched.roles && formik.errors.roles}
                    required
                  />
                )}
              />
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined' color='secondary' disabled={isLoading}>
          Cancel
        </Button>
        <Button type='submit' variant='contained' disabled={!formik.isValid || isLoading} loading={isLoading} onClick={formik.handleSubmit}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
